/**
 * Company workspace — Members management page (J-C2).
 *
 * Hiring managers can:
 *  - View current members (cursor-paginated)
 *  - Invite single members by email
 *  - Bulk invite by pasting email list
 *  - Remove members with confirmation dialog
 *
 * Uses Shell mode='company'. All interactive elements use shadcn primitives (Inv 15).
 */

import { useState, useCallback, useEffect, type ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { Shell } from '../../components/shell/Shell.tsx';
import { Button, Card, Input } from '@/components/ui';
import { apiFetch } from '@/lib/api-client';

interface MemberRow {
  id: string;
  user_id: string;
  /** API field name from the server — matches Member.user_email in company-quotas module. */
  user_email: string;
  role: string;
  status: string;
  created_at: string;
}

interface Page<T> {
  items: T[];
  next_cursor: string | null;
}

interface RemoveDialogState {
  member: MemberRow;
  submitting: boolean;
  error: string | null;
}

export function MembersPage(): ReactElement {
  const params = useParams<{ id: string }>();
  const companyId = params.id ?? '';

  // Member list state
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorKind, setErrorKind] = useState<'forbidden' | 'load_failure' | null>(null);

  // Single invite form
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<string>('member');
  const [inviteStatus, setInviteStatus] = useState<string | null>(null);

  // Bulk invite form
  const [bulkEmails, setBulkEmails] = useState('');
  const [bulkRole, setBulkRole] = useState<string>('member');
  const [bulkStatus, setBulkStatus] = useState<string | null>(null);

  // Remove confirmation dialog
  const [removeDialog, setRemoveDialog] = useState<RemoveDialogState | null>(null);

  const loadMembers = useCallback(
    async (nextCursor?: string | null) => {
      setIsLoading(true);
      setError(null);

      try {
        const q = new URLSearchParams();
        if (nextCursor) q.set('cursor', nextCursor);
        q.set('limit', '25');

        const response = await apiFetch(
          `/api/companies/${companyId}/members?${q.toString()}`,
          { credentials: 'include' },
        );

        if (response.status === 403) {
          setErrorKind('forbidden');
          setError('forbidden');
          return;
        }

        if (!response.ok) {
          setErrorKind('load_failure');
          setError('Failed to load members. Please try again.');
          return;
        }

        const data = (await response.json()) as {
          ok: boolean;
          data: Page<MemberRow>;
          error?: { code: string; message: string };
        };
        if (!data.ok) {
          if (data.error?.code === 'forbidden') {
            setErrorKind('forbidden');
            setError('forbidden');
            return;
          }
          setErrorKind('load_failure');
          setError('Failed to load members. Please try again.');
          return;
        }

        setErrorKind(null);
        if (nextCursor) {
          setMembers((prev) => [...prev, ...data.data.items]);
        } else {
          setMembers(data.data.items);
        }

        setCursor(data.data.next_cursor);
        setHasMore(!!data.data.next_cursor);
      } catch {
        setErrorKind('load_failure');
        setError('Failed to load members. Please try again.');
      } finally {
        setIsLoading(false);
      }
    },
    [companyId],
  );

  useEffect(() => {
    void loadMembers();
  }, [loadMembers]);

  // Single invite
  const handleSingleInvite = async (): Promise<void> => {
    if (!inviteEmail.trim()) return;
    setInviteStatus(null);
    try {
      const response = await fetch(
        `/api/companies/${companyId}/members/invite`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ email: inviteEmail.trim(), role: inviteRole }),
          credentials: 'include',
        },
      );
      const data = (await response.json()) as { ok: boolean; data?: { outcome: string }; error?: { message: string } };
      if (!data.ok) {
        setInviteStatus(`Error: ${data.error?.message ?? 'invite failed'}`);
        return;
      }
      setInviteStatus(`Invited — ${data.data?.outcome ?? 'sent'}`);
      setInviteEmail('');
      void loadMembers();
    } catch {
      setInviteStatus('Error: network error');
    }
  };

  // Bulk invite
  const handleBulkInvite = async (): Promise<void> => {
    const emails = bulkEmails
      .split(/[\n,;]+/)
      .map((e) => e.trim())
      .filter((e) => e.length > 0);
    if (emails.length === 0) return;
    setBulkStatus(null);
    try {
      const response = await fetch(
        `/api/companies/${companyId}/members/bulk-invite`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ emails, role: bulkRole }),
          credentials: 'include',
        },
      );
      const data = (await response.json()) as { ok: boolean; data?: { results: Array<{ outcome: string }> }; error?: { message: string } };
      if (!data.ok) {
        setBulkStatus(`Error: ${data.error?.message ?? 'bulk invite failed'}`);
        return;
      }
      const invited = data.data?.results?.filter((r) => r.outcome === 'invited').length ?? 0;
      setBulkStatus(`${invited} invited`);
      setBulkEmails('');
      void loadMembers();
    } catch {
      setBulkStatus('Error: network error');
    }
  };

  // Remove member
  const handleRemoveConfirm = async (): Promise<void> => {
    if (!removeDialog) return;
    setRemoveDialog({ ...removeDialog, submitting: true, error: null });
    try {
      const response = await fetch(
        `/api/companies/${companyId}/members/${removeDialog.member.user_id}`,
        {
          method: 'DELETE',
          credentials: 'include',
        },
      );
      const data = (await response.json()) as { ok: boolean; error?: { message: string } };
      if (!data.ok) {
        setRemoveDialog({
          ...removeDialog,
          submitting: false,
          error: data.error?.message ?? 'remove failed',
        });
        return;
      }
      setRemoveDialog(null);
      void loadMembers();
    } catch {
      setRemoveDialog({
        ...removeDialog,
        submitting: false,
        error: 'network error',
      });
    }
  };

  return (
    <Shell mode="company" densityMode="workspace" companyId={companyId}>
      <div className="space-y-8">
        <header>
          <p className="font-mono text-mono text-muted-2">COMPANY · MEMBERS</p>
          <h1 className="mt-2 font-sans text-headline font-medium tracking-tight">
            Members
          </h1>
          <p className="mt-2 max-w-prose font-sans text-body text-muted">
            Manage your team. Invite new members or remove existing ones.
          </p>
        </header>

        {/* Error state */}
        {error && errorKind === 'forbidden' && (
          <div className="rounded border border-line bg-card p-8 text-center" role="alert">
            <p className="font-sans text-title font-medium text-ink">You don&apos;t have access to this company.</p>
            <p className="mt-2 font-sans text-small text-muted">
              You are not an active member of this company workspace.
              Contact your workspace administrator to request access.
            </p>
          </div>
        )}
        {error && errorKind === 'load_failure' && (
          <div className="rounded border border-error/30 bg-error/5 p-4" role="alert">
            <p className="font-sans text-small text-error">
              Something went wrong loading the member list.
            </p>
            <Button variant="ghost" size="sm" type="button" onClick={() => void loadMembers()} className="mt-2">
              Retry
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* Invite forms — sidebar */}
          <aside className="space-y-6 lg:col-span-1">
            {/* Single invite */}
            <Card variant="default" className="p-4 space-y-4">
              <h2 className="font-sans text-title font-medium">Invite Member</h2>
              <div>
                <label htmlFor="invite-email" className="mb-1 block font-sans text-eyebrow font-medium uppercase tracking-wide text-muted">
                  Email
                </label>
                <Input
                  id="invite-email"
                  name="invite-email"
                  type="email"
                  placeholder="colleague@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="invite-role" className="mb-1 block font-sans text-eyebrow font-medium uppercase tracking-wide text-muted">
                  Role
                </label>
                <select
                  id="invite-role"
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="w-full rounded border border-line-2 bg-card px-3 py-2 font-sans text-body text-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent-l transition-colors duration-200"
                >
                  <option value="member">Member</option>
                  <option value="viewer">Viewer</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                  <option value="owner">Owner</option>
                </select>
              </div>
              <Button variant="primary" type="button" onClick={() => void handleSingleInvite()} className="w-full">
                Invite
              </Button>
              {inviteStatus && (
                <p className="font-sans text-small text-muted">{inviteStatus}</p>
              )}
            </Card>

            {/* Bulk invite */}
            <Card variant="default" className="p-4 space-y-4">
              <h2 className="font-sans text-title font-medium">Bulk Invite</h2>
              <div>
                <label htmlFor="bulk-emails" className="mb-1 block font-sans text-eyebrow font-medium uppercase tracking-wide text-muted">
                  Emails (one per line)
                </label>
                <textarea
                  id="bulk-emails"
                  name="bulk-emails"
                  placeholder={'alice@example.com\nbob@example.com'}
                  value={bulkEmails}
                  onChange={(e) => setBulkEmails(e.target.value)}
                  rows={4}
                  className="w-full rounded border border-line-2 bg-card px-3 py-2 font-sans text-body text-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent-l transition-colors duration-200"
                />
              </div>
              <div>
                <label htmlFor="bulk-role" className="mb-1 block font-sans text-eyebrow font-medium uppercase tracking-wide text-muted">
                  Role
                </label>
                <select
                  id="bulk-role"
                  value={bulkRole}
                  onChange={(e) => setBulkRole(e.target.value)}
                  className="w-full rounded border border-line-2 bg-card px-3 py-2 font-sans text-body text-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent-l transition-colors duration-200"
                >
                  <option value="member">Member</option>
                  <option value="viewer">Viewer</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <Button variant="primary" type="button" onClick={() => void handleBulkInvite()} className="w-full">
                Bulk Invite
              </Button>
              {bulkStatus && (
                <p className="font-sans text-small text-muted">{bulkStatus}</p>
              )}
            </Card>
          </aside>

          {/* Member list */}
          <section className="lg:col-span-2" aria-label="Members list">
            {isLoading && members.length === 0 ? (
              <div className="rounded border border-line bg-card p-8 text-center">
                <p className="font-sans text-body text-muted">Loading members…</p>
              </div>
            ) : members.length === 0 ? (
              <div className="rounded border border-line bg-card p-8 text-center">
                <p className="font-sans text-body text-muted">No members yet.</p>
                <p className="mt-2 font-sans text-small text-muted">Use the invite form to add team members.</p>
              </div>
            ) : (
              <Card variant="default" className="overflow-hidden p-0">
                <table className="w-full">
                  <thead className="border-b border-line bg-bg-warm">
                    <tr>
                      <th scope="col" className="px-4 py-3 text-left font-sans text-eyebrow font-medium uppercase tracking-wide text-muted">
                        Email
                      </th>
                      <th scope="col" className="px-4 py-3 text-left font-sans text-eyebrow font-medium uppercase tracking-wide text-muted">
                        Role
                      </th>
                      <th scope="col" className="px-4 py-3 text-left font-sans text-eyebrow font-medium uppercase tracking-wide text-muted">
                        Status
                      </th>
                      <th scope="col" className="px-4 py-3 text-right font-sans text-eyebrow font-medium uppercase tracking-wide text-muted">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((member) => (
                      <tr key={member.id} className="border-b border-line last:border-b-0">
                        <td className="px-4 py-3 font-sans text-body text-ink">
                          {member.user_email}
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex rounded-sm border border-line-2 bg-bg-warm px-2 py-0.5 font-mono text-xs uppercase tracking-wide text-muted">
                            {member.role}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex rounded-sm px-2 py-0.5 font-mono text-xs uppercase tracking-wide ${
                            member.status === 'active'
                              ? 'bg-success/10 text-success border border-success/30'
                              : 'bg-muted/10 text-muted border border-muted/30'
                          }`}>
                            {member.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {member.role !== 'owner' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              type="button"
                              onClick={() => setRemoveDialog({ member, submitting: false, error: null })}
                              className="font-medium text-error hover:text-error"
                            >
                              Remove
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {hasMore && (
                  <div className="border-t border-line px-4 py-3 text-center">
                    <Button variant="ghost" type="button" onClick={() => void loadMembers(cursor)}>
                      Load more
                    </Button>
                  </div>
                )}
              </Card>
            )}
          </section>
        </div>
      </div>

      {/* Remove confirmation dialog */}
      {removeDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" role="dialog" aria-modal="true" aria-label="Remove member">
          <Card variant="default" className="mx-4 w-full max-w-md">
            <h2 className="mb-4 text-h3 font-medium">Remove member</h2>
            <p className="mb-4 font-sans text-small text-muted">
              Are you sure you want to remove{' '}
              <span className="font-medium text-ink">{removeDialog.member.user_email}</span>{' '}
              from this company?
            </p>
            {removeDialog.error && (
              <div className="mb-4 font-sans text-small text-error" role="alert">
                {removeDialog.error}
              </div>
            )}
            <div className="flex justify-end gap-3">
              <Button variant="secondary" type="button" onClick={() => setRemoveDialog(null)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                type="button"
                onClick={() => void handleRemoveConfirm()}
                disabled={removeDialog.submitting}
                className="bg-error text-white hover:bg-error/90"
              >
                {removeDialog.submitting ? 'Removing…' : 'Confirm'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </Shell>
  );
}
